import { motion } from "framer-motion";
import { Flame, CookingPot } from "lucide-react";
import { IMAGES } from "@/brand";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const Chip = ({ children }) => (
    <span className="rounded-full border border-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-sand">
        {children}
    </span>
);

const ProductCard = ({ image, alt, badge, badgeTestid, cardTestid, title, sub, desc, chips, heat }) => (
    <motion.article
        initial={{ opacity: 0, y: 48 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="group overflow-hidden rounded-3xl border border-white/10 bg-soot transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:border-nutmeg/40 hover:shadow-[0_32px_80px_-32px_rgba(234,179,8,0.25)]"
        data-testid={cardTestid}
    >
        <div className="relative h-80 overflow-hidden sm:h-96">
            <img
                src={image}
                alt={alt}
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-soot via-obsidian/20 to-transparent" />
            <span
                className="absolute left-5 top-5 rounded-full bg-bonnet px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-parchment shadow-lg"
                data-testid={badgeTestid}
            >
                {badge}
            </span>
        </div>
        <div className="p-8 sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nutmeg">{sub}</p>
            <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-parchment sm:text-3xl">
                {title}
            </h3>
            <p className="mt-4 text-base leading-relaxed text-sand">{desc}</p>
            <div className="mt-6 flex flex-wrap gap-2">
                {chips.map((c) => (
                    <Chip key={c}>{c}</Chip>
                ))}
            </div>
            <div className="mt-8 flex items-center gap-2 border-t border-white/10 pt-6">
                <span className="mr-2 text-xs font-semibold uppercase tracking-[0.2em] text-sand/60">Heat</span>
                {Array.from({ length: 5 }).map((_, i) => (
                    <Flame
                        key={i}
                        className={`h-4 w-4 ${i < heat ? "text-bonnetglow" : "text-white/15"}`}
                        fill={i < heat ? "currentColor" : "none"}
                    />
                ))}
            </div>
        </div>
    </motion.article>
);

export const Products = () => (
    <section id="products" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36" data-testid="products-section">
        <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="max-w-2xl"
        >
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-bonnetglow">The first batch</p>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-parchment sm:text-5xl lg:text-6xl">
                Coming soon <span className="font-editorial font-medium normal-case italic text-nutmeg">in Ontario</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-sand sm:text-lg">
                Two jars of island fire, made in small batches and packed with whole spices. Not on shelves yet —
                this is your first look before the Ontario launch.
            </p>
        </motion.div>

        <div className="mt-16 grid gap-8 lg:grid-cols-2 lg:gap-10">
            <ProductCard
                image={IMAGES.jerkJar}
                alt="Signature Jerk Seasoning jar"
                badge="Coming soon in Ontario"
                badgeTestid="product-badge-coming-soon-jerk"
                cardTestid="product-card-jerk"
                sub="Signature · 283.5 g (10 oz) jar"
                title="Signature Jerk Seasoning"
                desc="Our family's jerk, the way it has always been made — scotch bonnet fire, pimento smoke and fresh thyme, ground and blended in small batches. Rub it, marinate it, or spoon it straight over the grill."
                chips={["Scotch bonnet", "Pimento", "Fresh thyme"]}
                heat={4}
            />
            <ProductCard
                image={IMAGES.bbqBottle}
                alt="Signature BBQ Sauce jar"
                badge="Launching soon in Ontario"
                badgeTestid="product-badge-coming-soon-bbq"
                cardTestid="product-card-bbq"
                sub="Small-batch · 283.5 g (10 oz) jar"
                title="Signature BBQ Sauce"
                desc="A slow, dark BBQ sauce built on smoked molasses and Grenada nutmeg — sweet first, then the smoke rolls in. Brushes on thick and lacquers beautifully over low heat."
                chips={["Smoked molasses", "Grenada nutmeg", "Slow smoke"]}
                heat={2}
            />
        </div>

        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-14 flex flex-wrap items-center gap-6 rounded-3xl border border-nutmeg/20 bg-coal p-8"
            data-testid="products-notify-strip"
        >
            <CookingPot className="h-8 w-8 shrink-0 text-nutmeg" />
            <p className="flex-1 text-base leading-relaxed text-sand">
                No online ordering yet — the first jars go to the launch list. Leave your email and you'll hear the
                moment we open the kitchen doors in Ontario.
            </p>
            <button
                onClick={() => scrollToId("launch-list")}
                className="rounded-full border border-nutmeg/60 px-6 py-3 text-xs font-bold uppercase tracking-[0.16em] text-nutmeg transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-nutmeg hover:text-obsidian"
                data-testid="products-notify-button"
            >
                Get notified
            </button>
        </motion.div>
    </section>
);

export default Products;
